import React, { useRef, useState, useCallback } from 'react';
import { Box, Typography, Grid, TextField, FormControl, RadioGroup, FormControlLabel, Radio, Button, Paper, Chip, Divider, Autocomplete, CircularProgress, MenuItem, Checkbox, FormGroup } from '@mui/material';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { sanitizePhoneDigits, formatPhoneProgressive, isCompletePhone } from './phoneUtils';

// Section wrapper for consistent styling
const Section = ({ number, title, children, noDivider }) => (
  <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2, position: 'relative' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: title ? 1.5 : 0 }}>
      {number !== undefined && (
        <Chip label={number} size="small" color="primary" sx={{ mr: 1, fontWeight: 600 }} />
      )}
      {title && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      )}
    </Box>
    {children}
    {!noDivider && <Divider sx={{ mt: 2, opacity: 0.3 }} />}
  </Paper>
);

// Redesigned OB Form with sectioned layout
// hideShared: hides "Your information" (1) and "Patient Information" (2) when those
//   fields are collected elsewhere (e.g. the redesigned portal's shared section 2).
// hideActions: hides the built-in Print button when actions are owned by a parent (modal).
const OBForm = ({ form, onChange, onSubmit, title = 'OB Intake Form', faxInstructions, hideActions = false }) => {
  const contentRef = useRef(null);
  const handlePrint = useReactToPrint({ contentRef });
  // Address autocomplete state
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const BASE_API = process.env.REACT_APP_API_URL || '';
  const RACE_OPTIONS = [
    'Hispanic/Latino',
    'Non-Hispanic/Latino',
    'Other',
    'Unknown',
  ];
  const INSURANCE_OPTIONS = [
    'Self-pay / Uninsured',
    'Medicaid/CHIP',
    'Medicare only',
    'Medicare + supplemental',
    'Dual eligible (Medicare + Medicaid)',
    'Other third party',
    'Unknown',
  ];
  const REFERRED_SERVICES = [
    'Primary Care',
    'Case Management',
    'Mental/Behavioral Health Treatment/Education',
    'Substance Use Treatment/Education',
    'Health Education',
    'Nutrition',
    'Telehealth',
    'Transportation',
    'Culturally Competent Services',
    'Health Literacy',
    'Maternal & Child Health',
    'Oral Health (Education/Treatment)',
    'Cardiovascular Disease / Hypertension',
    'Diabetes',
    'Obesity',
    'Emergency Medical Services',
    'Workforce Recruitment/Retention',
  ];
  const UNMET_NEEDS = [
    'Medical Care',
    'Dental Care',
    'SUD/Mental Health Care',
    'Medication Assistance',
    'Transportation',
    'Application Assistance (Medicaid, SNAP, etc.)',
    'Help with Child Needs',
    'Unsafe Housing',
    'Housing Assistance',
    'Additional Education',
    'Interpretation / Translation',
    'Advocacy',
  ];

  const fetchAddressSuggestions = useCallback(async (input) => {
    if (!input || input.trim().length < 3) { setAddressOptions([]); return; }
    setAddressLoading(true);
    try {
      const resp = await axios.get(`${BASE_API}/maps`, { params: { input } });
      setAddressOptions(resp.data?.predictions || []);
    } catch(e){
      setAddressOptions([]);
    } finally { setAddressLoading(false); }
  }, [BASE_API]);

  const parseComponent = (components, type, short=false) => {
    const c = components.find(comp => comp.types.includes(type));
    if (!c) return '';
    return short ? c.short_name : c.long_name;
  };

  const handleSelectAddress = useCallback(async (option) => {
    if (!option) return;
    try {
      const resp = await axios.get(`${BASE_API}/maps/details`, { params: { place_id: option.place_id } });
      const result = resp.data?.result;
      if (!result?.address_components) return;
      const comps = result.address_components;
      const streetNum = parseComponent(comps, 'street_number');
      const route = parseComponent(comps, 'route');
      const formattedAddress = [streetNum, route].filter(Boolean).join(' ');
      const city = parseComponent(comps, 'locality');
      const state = parseComponent(comps, 'administrative_area_level_1', true);
      // Fire individual change events to parent
      if (formattedAddress) onChange({ target: { name: 'address', value: formattedAddress } });
      if (city) onChange({ target: { name: 'city', value: city } });
      if (state) onChange({ target: { name: 'state', value: state } });
    } catch(e){ /* ignore */ }
  }, [BASE_API, onChange]);

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Box
        component="form"
        ref={contentRef}
        onSubmit={onSubmit}
        sx={{ maxWidth: 900, mx: 'auto', px: { xs: 1.5, sm: 3 }, pt: 1 }}
      >
        <Section number={1} title="Your information">

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth label="Your name" name="submitterName" value={form.submitterName||''} onChange={onChange} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth label="Your contact number" name="submitterContact" value={form.submitterContact||''} onChange={onChange} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth label="Your Email" type="email" name="submitterEmail" value={form.submitterEmail||''} onChange={onChange} />
            </Grid>
          </Grid>
        </Section>

        <Section number={2} title="Patient Information">
          <Grid container spacing={2}>
            {/* Row 1: Name / DOB / SS */}
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <TextField fullWidth label="Patient Name" name="patientName" value={form.patientName} onChange={onChange} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 3 }}>
              <TextField fullWidth label="DOB" type="date" name="dob" InputLabelProps={{ shrink: true }} value={form.dob} onChange={onChange} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 3 }}>
              <TextField fullWidth label="SS/TIN" name="ssTin" value={form.ssTin} onChange={onChange} />
            </Grid>

            {/* Row 2: Address full width */}
            <Grid size={12}>
              <Autocomplete
                fullWidth
                freeSolo={false}
                options={addressOptions}
                loading={addressLoading}
                getOptionLabel={(opt) => opt?.description || ''}
                value={null}
                inputValue={form.address || ''}
                onInputChange={(e, value, reason) => {
                  if (reason === 'input') {
                    onChange({ target: { name: 'address', value } });
                    fetchAddressSuggestions(value);
                  }
                }}
                onChange={(e, val) => handleSelectAddress(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Address"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {addressLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Row 3: City / State / Phone */}
            <Grid size={{ xs: 12, sm: 5, md: 5 }}>
              <TextField fullWidth label="City" name="city" value={form.city} onChange={onChange} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2, md: 2 }}>
              <TextField fullWidth label="State" name="state" value={form.state} onChange={onChange} />
            </Grid>
            <Grid size={{ xs: 6, sm: 5, md: 5 }}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formatPhoneProgressive(form.phone || '')}
                error={!!form.phone && !isCompletePhone(form.phone)}
                onChange={(e)=>{
                  const digits = sanitizePhoneDigits(e.target.value);
                  onChange({ target: { name: 'phone', value: digits } });
                }}
                inputProps={{ inputMode: 'tel' }}
              />
            </Grid>

            {/* Row 4: Due Date / Preferred Language / RID */}
            <Grid size={{ xs: 12, sm: 4, md: 4 }}>
              <TextField fullWidth label="Estimated Due Date" type="date" name="dueDate" InputLabelProps={{ shrink: true }} value={form.dueDate} onChange={onChange} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 4 }}>
              <TextField fullWidth label="Preferred Language" name="preferredLanguage" value={form.preferredLanguage} onChange={onChange} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Race"
                name="race"
                value={form.race || ''}
                onChange={onChange}
              >
                {RACE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Row 5: Insurance Status / HB Intervention */}
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Insurance Status"
                name="insuranceStatus"
                value={form.insuranceStatus || ''}
                onChange={onChange}
              >
                {INSURANCE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <TextField fullWidth label="HB Intervention" name="hbIntervention" value={form.hbIntervention} onChange={onChange} />
            </Grid>
          </Grid>
        </Section>

        <Section number={3} title="Referred to">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Agency Name" name="referredAgencyName" value={form.referredAgencyName || ''} onChange={onChange} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Agency Address (if known)" name="referredAgencyAddress" value={form.referredAgencyAddress || ''} onChange={onChange} />
            </Grid>
          </Grid>
        </Section>

        <Section number={4} title="Scheduling">
          <Autocomplete
            options={[...REFERRED_SERVICES, 'Other']}
            value={form.referredServices || null}
            onChange={(e, value) => onChange({ target: { name: 'referredServices', value: value || '' } })}
            renderInput={(params) => (
              <TextField {...params} label="Referred for following services" placeholder="Select a service" />
            )}
            sx={{ mb: 2 }}
          />
          {form.referredServices === 'Other' && (
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Please specify other referred services"
              name="referredServicesOther"
              value={form.referredServicesOther || ''}
              onChange={onChange}
            />
          )}

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={[...UNMET_NEEDS, 'Other']}
            value={form.unmetNeeds || []}
            onChange={(e, value) => onChange({ target: { name: 'unmetNeeds', value } })}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} sx={{ mr: 1 }} />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Unmet Needs" placeholder="Select unmet needs" />
            )}
          />
          {(form.unmetNeeds || []).includes('Other') && (
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="Please specify other unmet needs"
              name="unmetNeedsOther"
              value={form.unmetNeedsOther || ''}
              onChange={onChange}
            />
          )}
        </Section>

        {faxInstructions && (
          <Section number={5} title="Fax Instructions" noDivider>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {faxInstructions}
            </Typography>
          </Section>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1, mb: 2 }}>
          {/* <Button variant="contained" type="submit">Submit</Button> */}
          {!hideActions && <Button variant="outlined" onClick={handlePrint}>Print Form</Button>}
        </Box>
      </Box>
    </Box>
  );
};

export default OBForm;
